// ─────────────────────────────────────────────────────────────────────────────
// presentation.ts — the PRESENTATION layer for a declaration-driven domain
// surface. Owner ruling: "Job titles are PRESENTATION. The identifiers are the
// agent names and nothing renames."
//
// WHAT THIS FILE IS ALLOWED TO BE, AND WHAT IT IS NOT:
//
//   IT IS      a lookup of how to DISPLAY something the API already returned —
//              which rail group a declared root belongs in, and what a human
//              reads instead of a bare slug.
//   IT IS NOT  a source of truth for WHAT EXISTS. Roots come from the topology
//              via /api/os/structure; the roster comes from the registry via the
//              same call. Nothing is rendered because it appears below, and
//              nothing is hidden because it does not.
//
// THE DEGRADE RULE IS THE WHOLE CONTRACT: an entry missing from these tables is
// STILL RENDERED — a root the OS declares next week appears as a tab with its own
// label, and an agent added next week appears in the roster under its own name.
// A table that could swallow an unknown would be the "check that lists known
// agents and passes over the one added next week" this surface exists to avoid.
// ─────────────────────────────────────────────────────────────────────────────

/** The three rail groups, in render order. Headers are the owner's own words. */
export const RAIL_GROUPS = [
  'OVERVIEW',
  'DOMAIN CONTROLLERS',
  // Owner ruling (2026-08-26): APPS is a group because OSIRIS IS NOT AN AGENT.
  // It was to go "beside Domain CEO, Marketing and Sales" — but those three are
  // rendered as AGENT rows, and DOMAIN CONTROLLERS is a claim about what a row
  // IS, not about where it sits. Filing a non-agent under that header would have
  // the rail assert the one thing the root is defined by not being. Its own
  // group puts it beside them without saying it is one of them.
  'APPS',
  'RECEPTIONIST AGENTS',
  // Owner ruling (2026-08-19): the cross-domain SERVICE and CODE-team cards rode
  // on the generic screen chrome that a full surface replaces. "Unreachable is
  // worse than out of place" — they come back under their own group rather than
  // being squeezed into one of the design's three.
  'CROSS-DOMAIN',
] as const;
export type RailGroup = typeof RAIL_GROUPS[number];

/** The accent a rail row and its icon badge carry. Names a PALETTE TOKEN, never a
 *  literal — the sampled values live in theme.ts and are not restated here. */
export type RailAccent = 'teal' | 'blue' | 'violet' | 'amber' | 'green';

export interface RailPlacement {
  group: RailGroup;
  label: string;
  /** Two lines, newline-separated. The design's two-line descriptor. */
  desc?: string;
  accent?: RailAccent;
}

/**
 * Root slug → its rail placement. A declared root ABSENT from this map is a TAB
 * ONLY — it still renders across the top strip, it simply has no left-rail row.
 * That is a presentation decision about the rail, never a filter on what exists.
 */
export const ROOT_RAIL: Record<string, RailPlacement> = {
  overview:    { group: 'OVERVIEW',           label: 'Overview',             desc: 'domain landing\nall agents rolled up',        accent: 'teal'   },
  ceo:         { group: 'DOMAIN CONTROLLERS', label: 'Domain CEO',           desc: 'daily report\ngap escalation',               accent: 'violet' },
  marketing:   { group: 'DOMAIN CONTROLLERS', label: 'Marketing Agent',      desc: 'campaigns\nregions and maps',                accent: 'blue'   },
  sales:       { group: 'DOMAIN CONTROLLERS', label: 'Sales Agent',          desc: 'qualification\npipeline movement',           accent: 'green'  },
  admin_fleet: { group: 'DOMAIN CONTROLLERS', label: 'Admin Fleet Overview', desc: 'attached admins\nworkload rollup',           accent: 'amber'  },
  // OSIRIS — an APP the owner opens, not an agent. Absent from ROLE_ROOT on
  // purpose (see below): that absence is what makes rootOwner null, which is
  // what withholds the DASHBOARD | CHAT bar. Nothing was added to suppress chat;
  // chat is simply never claimed. The accent is a RAIL token, not a sector
  // colour — the design assigns sectors no colour and this build assigns none.
  osiris:      { group: 'APPS',               label: 'Osiris',               desc: 'cross-domain map\nreads Marketing and Sales', accent: 'teal'  },
};

/**
 * Panel → the board root its content lives under. The board is keyed by root
 * SLUG (the PUT choke derives the changed root by diffing these very keys), so a
 * panel reads board[root] and renders empty-state until that root's page ships.
 *
 * FLAGGED FOR THE OWNER, not assumed settled: `telco` is keyed to
 * supporting_systems and `apiWatch` to integrations. Both roots are empty today,
 * so the mapping costs nothing yet and is ONE constant each to correct.
 */
export const PANEL_ROOT = {
  kpis:   'overview',
  budget: 'pricing_budgets',
  ceo:    'ceo',
  telco:  'supporting_systems',
  apiWatch: 'integrations',
} as const;

/** The five KPI tiles, in the owner's stated order. */
export const KPI_TILES = [
  { key: 'calls',           label: 'CALLS' },
  { key: 'emails',          label: 'EMAILS' },
  { key: 'sms',             label: 'SMS' },
  { key: 'voice_transport', label: 'VOICE TRANSPORT' },
  { key: 'ceo_gaps',        label: 'CEO GAPS' },
] as const;

/**
 * THE ICON BADGE, DERIVED — never a per-agent or per-root icon table.
 *
 * A table mapping a name to a glyph is exactly the "check that lists known agents
 * and passes over the one added next week": the agent registered tomorrow would
 * fall through it and render blank. So the badge is DERIVED FROM THE IDENTIFIER
 * ITSELF — up to two initials off the words of the label. Everything that exists
 * gets a badge, including things this file has never heard of.
 */
export function badgeOf(label: string): string {
  const words = String(label).trim().split(/[^A-Za-z0-9]+/).filter(Boolean);
  if (words.length === 0) return '·';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * The two mini-stat tiles pinned bottom-left.
 *
 * SHAPE FROM THE DESIGN, VALUES FROM THE OS. The reference image's numbers are
 * sample data (owner ruling, this session) so nothing is transcribed from it —
 * each tile names a COUNT the surface can honestly derive from what the API
 * returned, and reads "—" before the read lands.
 */
export const MINI_STATS = [
  { key: 'calls_today',  label: 'CALLS TODAY'  },
  { key: 'voice_spend',  label: 'VOICE SPEND'  },
] as const;

/**
 * Declared ROLE id → the declared ROOT slug that already renders it as a
 * controller row. Two declared vocabularies joined; NEITHER is invented here and
 * no agent is named.
 *
 * THE DEGRADE RULE IS INVERTED ON PURPOSE, AND THAT IS WHAT MAKES IT SAFE:
 * absence means SHOW THE AGENT, never hide it. A role id missing from this map is
 * read as "not represented by a controller row", so the agent renders in the
 * roster group under its own name and its own declared role. The failure this
 * avoids is the table that quietly swallows the agent added next week — here the
 * worst an omission can do is show an agent twice, which is visible, rather than
 * not at all, which is not.
 *
 * `receptionist` is deliberately ABSENT: a receptionist has no controller row, so
 * it always falls through to the roster group. That is the design's third group.
 *
 * NOTHING MAPS TO `osiris`, AND THAT ABSENCE IS THE MECHANISM. This map is keyed
 * by ROLE, and an app has no role — so `rootForAgent` returns null for every
 * agent, `rootOwner` resolves null on that root, and the DASHBOARD | CHAT bar
 * behind `{rootOwner && …}` never renders. No agent id is held, no turn is
 * possible and no chat is reachable BECAUSE NOTHING WAS ADDED HERE, not because
 * something suppresses it. A future entry pointing a role at `osiris` would hand
 * the app an owner and a chat door in one edit — which is the thing to notice.
 * Four roots already render this way: overview, integrations, supporting_systems
 * and pricing_budgets.
 */
export const ROLE_ROOT: Record<string, string> = {
  domain_ceo:  'ceo',
  marketing:   'marketing',
  sales:       'sales',
  admin_fleet: 'admin_fleet',
};

/**
 * Declared ROLE id → the FUNCTION line beneath the role, and the row's accent.
 *
 * THE NAME IS THE AGENT'S NAME. Owner ruling (2026-08-19): "ALPHA is the name.
 * 'Domain CEO' is what ALPHA does." A rail row therefore reads
 *
 *     ALPHA                 ← the agent's own name, from the registry
 *     Domain CEO            ← role.label, the DECLARED job title
 *     read-only pulse · …   ← the function line, below
 *
 * and never puts the job title where the name belongs. `label` is a descriptor;
 * using it as the row title renamed four agents on screen.
 *
 * KEYED BY ROLE ID — a DECLARED vocabulary — never by agent name. Two agents
 * sharing a role share this line, which is correct: DOE and ILSA are both
 * receptionists. THE DEGRADE RULE HOLDS: a role id absent from this table still
 * renders its row, with the declared label and no function line. Nothing is
 * hidden for being unrecognised.
 */
export const ROLE_RAIL: Record<string, { fn?: string; accent: RailAccent }> = {
  domain_ceo:   { accent: 'violet', fn: 'read-only pulse · reports · dreamstate' },
  marketing:    { accent: 'blue',   fn: 'discovery · qualification · Sales handoff' },
  sales:        { accent: 'green',  fn: 'research · calls · consultation · close' },
  admin_fleet:  { accent: 'amber',  fn: 'one attached Admin Agent per receptionist' },
  receptionist: { accent: 'violet' },
};

// ─────────────────────────────────────────────────────────────────────────────
// THE FIT BAND — a READER concern, not a data one.
//
// The qualification board's Fit column is a PILL in the design, and the tone is
// carried by the number. The backend deliberately derives no threshold and it is
// right not to: what a score LOOKS like is presentation, and a writer that stamped
// a colour would be deciding the reader's business — and would have to be
// re-deployed to restyle a cell.
//
// ── THE FOUR SAMPLES, WHICH ARE ALL THE DESIGN GIVES ────────────────────────
//   <span class="pill green">9.28</span>     Rapid Lock Metro
//   <span class="pill green">8.73</span>     Metro Auto Electrical
//   8.45                                     Bright Paw Vet — NO PILL, plain text
//   <span class="pill amber">7.95</span>     Solo Mobile Auto
//
// Three visual states from four rows. The boundaries below are the roundest pair
// consistent with ALL FOUR — 8.73 green and 8.45 bare puts the upper edge in
// (8.45, 8.73]; 8.45 bare and 7.95 amber puts the lower edge in (7.95, 8.45].
// They are declared here as the reader's rule, not inferred per render.
//
// NO RED BAND. The design shows no red Fit anywhere, so none is invented — a
// fourth state would be this file deciding something the design did not say. If
// the owner wants one, it is one line and one sample.
//
// THE DEGRADE RULE HOLDS, as everywhere else in this file: a value that is not a
// finite number returns null and the cell renders EXACTLY as it does today, its
// own text, unstyled. A band table must never be able to swallow a cell.
// ─────────────────────────────────────────────────────────────────────────────

export type PillTone = 'green' | 'amber';

export const FIT_BANDS: { min: number; tone: PillTone | null }[] = [
  { min: 8.5, tone: 'green' },
  { min: 8.0, tone: null },     // banded, and deliberately UNPILLED — the design's 8.45
  { min: -Infinity, tone: 'amber' },
];


// ─────────────────────────────────────────────────────────────────────────────
// STATE PILLS — SAMPLED PER SLOT, AND PER SLOT IS THE WHOLE POINT.
//
// A SLOT IS THE NAMED PLACE A VALUE ARRIVES IN, and it is deliberately NOT
// called a column. For a table cell the slot happens to be the column's label,
// which is why this read as "column" while tables were the only reader. A ledger
// row has no columns — its state arrives in a slot literally named `state`, and
// calling that a column would make one word mean two things for the next reader.
// The axis is the slot; a column label is just what a table's slot is called.
//
// The design tones a state word with a pill. The obvious build is one global
// word -> tone table, AND IT IS WRONG. Three hazards, all found in the source:
//
//   1 · THE SAME WORD CARRIES TWO TONES. "low" is green in one slot and teal
//       in another; "sent" is green in one and amber in another. A global table
//       has to pick one and is then silently wrong in the other place.
//   2 · CASE IS MEANING. "High" is amber (a priority) and "high" is red (a
//       severity). One lowercasing collapses two different facts into one.
//   3 · A WORD MEANS WHAT ITS ROW MEANS. "blocked" is GREEN — because its row is
//       "Duplicate conflicts blocked", where blocking is the good outcome. The
//       same word in a pipeline slot would mean the opposite. The design only
//       samples it once, so a global table would carry that green everywhere.
//
// So the vocabulary is keyed PANEL -> SLOT -> exact text. A word can only ever
// tone the slot it was sampled in, and cannot bleed into another.
//
// 'plain' IS A REAL STATE, NOT AN ABSENCE. The design also uses an UNTONED pill
// (class="pill" with no colour) — "medium", "Low", "registration", "known". That
// is a third visual level between a coloured pill and bare text, and flattening
// it to bare text loses a distinction the design drew deliberately.
//
// THE DEGRADE RULE, AS EVERYWHERE: a value absent from this table renders as its
// own text, unstyled. The table may tone a cell; it may never swallow one.
//
// GENERATED BY READING THE DESIGN, NOT BY HAND. Every entry below is a
// (slot, text, tone) triple lifted from the HTML — from a table cell where the
// slot is a column label, and from a ledger row where the slot is `state`.
//
// ── TWO EXCLUSION RULES, AND WHY EACH EXISTS ────────────────────────────────
// 1 · THE SAME TEXT TWICE IN THE SAME SLOT WITH TWO TONES is refused outright,
//     never resolved by keeping whichever was read last. The design contains
//     exactly one — "low" under "F · Crowding", green AND teal — and it is absent
//     below for that reason. Crowding is a TRAJECTORY there ("2 / low",
//     "0 → 1 / low"), so it needs a rule of its own, not a word map.
//     STILL REFUSED, AND STILL THE DESIGN'S ONLY ONE — re-measured this round
//     across every non-table pill as well, and no second collision exists.
// ── A STANDING RULE, GENERAL AND NOT A ONE-OFF (owner ruling 2026-08-25) ────
//
//   THE DESIGN SPECIFIES WHAT SOMETHING LOOKS LIKE.
//   IT DOES NOT GET TO SPECIFY HOW A LIVE VALUE IS DERIVED FROM PROSE.
//
// The design is the app, and where it has a sample the sample governs — but that
// authority is over APPEARANCE. When the design also writes a DERIVATION, the
// derivation is a separate claim and it has to survive real data on its own.
//
// The instance that produced the rule: the dispatch board decides `ready` vs
// `busy` with a substring sniff on the unit's own prose —
//
//     x.includes('ready') || x.includes('available') ? 'ready' : 'busy'
//
// under which "bay 3 · not available" reads READY. It matches the design's four
// samples and nothing live. So the SHAPE is taken and the RULE is refused: that
// state arrives from the board the way a KPI's `direction` does.
//
// This is the same fault as exclusion 2 below, one level up — there a word map
// would only ever match the design's own sample, here a parser would. Whenever
// the design appears to answer "how is this value decided", it is answering
// "how did I fill my own mock", and the two are not the same question.
//
// 2 · MEASUREMENT-SHAPED TEXT is excluded: "72%", "20 / 20", "decay day 15",
//     "1 degraded". Encoding those literals would only ever match the design's own
//     sample and never a live value. Those slots need a band like Fit's.
//     A P-CODE IS NOT A MEASUREMENT — "P1", "P2", "P1 · LIVE", "P1 · POST" are
//     fixed phase vocabulary the design reuses, and are kept.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A VISUAL, not just a colour — because the design draws TWO different elements
 * in this vocabulary and the difference is deliberate.
 *
 * The five bare names are PILLS: a table cell, a ledger state, a question tag
 * and a title-slot pill all render as one. The five `badge*` names are the
 * design's BADGE — smaller, letter-spaced, its own geometry — which the panel
 * title slot uses for 38 of its 41 elements.
 *
 * ELEMENT IS NOT DERIVABLE FROM COLOUR, which is why it rides in the token
 * rather than in a table beside it: the design draws `green` as a pill in a
 * ledger row and as a badge in a panel title. One token says both things at
 * once, so there is still ONE structure and nothing to keep in step.
 *
 * (Renamed from `StateTone` on the reasoning the slot rename followed: it now
 * carries an element as well as a tone, and one word meaning two things is the
 * fault that rename existed to remove.)
 */
export type Visual =
  | 'green' | 'amber' | 'red' | 'teal' | 'plain'
  | 'badgePlain' | 'badgeAmber' | 'badgeViolet' | 'badgeGreen' | 'badgeRed';

// ── THE PANEL TITLE'S `badge` SLOT — one slot, five tones, TWO ELEMENTS ─────
// S.panelBadge used to hardcode violet for EVERY badge on the surface. The
// design gives its title slot 41 elements across five tones, and only FIVE are
// violet — one hardcode standing in for the whole vocabulary.
//
// MEASURED by balanced-parsing every title block (a regex window leaked ledger
// rows and question tags into an earlier count, and was wrong by 46):
//   18 badge plain · 10 badge amber · 5 badge violet · 3 badge green ·
//   2 badge red · 3 PILL teal
// The three pills are the connector registries. They are a different ELEMENT,
// not a differently-coloured badge, which is why the token carries the element.
//
// ZERO AMBIGUITY, measured both ways: no text carries two visuals anywhere in
// the slot, and none does within a panel either. Keyed per panel regardless —
// the doctrine does not bend because one slot happens to be unambiguous today.
//
// ALL 33 THAT MAP TO A PANEL ARE HERE, not only the 12 the surface declares a
// badge for (owner ruling 2026-08-25: read all of it, because a badge the
// surface does not yet declare will want the same treatment when it arrives).
// An entry whose panel declares no badge text simply never fires.
export const STATE_PILLS: Record<string, Record<string, Record<string, Visual>>> = {
  acceptance_gate: {
    'badge': { 'packet accepted': 'badgeGreen' },
    'State': { 'inferred': 'amber', 'passed': 'green' },
  },
  active_prospect_work: {
    'State': { 'boundary review': 'amber', 'cold call ready': 'green', 'consultation ready': 'teal' },
  },
  alert_email_notifications: {
    'Status': { 'Delivered': 'green' },
  },
  api_change_watch: {
    'badge': { '2 priority items': 'badgeAmber' },
  },
  api_monitoring_path: {
    'badge': { 'deterministic': 'badgePlain' },
  },
  api_monitoring_queue: {
    'Priority': { 'high': 'red', 'medium': 'plain', 'review': 'amber' },
  },
  appointment_management: {
    'Status': { 'blocked': 'green', 'confirmed': 'green' },
  },
  // ── LEDGER ROWS · read from EIGHT rows across TWO panels ──────────────────
  // The ledger's slot is `state` — the row's trailing pill in the design. Two
  // panels, four sampled rows each, so this vocabulary is READ rather than
  // derived from a single instance the way the measurement bands had to be.
  //
  // PER-PANEL KEYING IS DOING REAL WORK HERE. `complete` is green in
  // event_ledger and is NOT sampled in audit_compliance — so it stays unstyled
  // there rather than bleeding across on a word match.
  audit_compliance: {
    'state': { 'allowed': 'green', 'logged': 'plain', 'pending': 'amber' },
  },
  auth_change_detection: {
    'badge': { '1 active': 'badgeRed' },
  },
  // ── BANNER PILLS · the slot is `banner`, keyed by the pill's LABEL ────────
  // Moved off an inline `tone` on the pill (owner ruling 2026-08-25). One entry
  // today; the 13 banners still to transcribe carry 33 more, and they land on a
  // shape that is already right rather than multiplying the old one.
  //
  // A pill with only a `label` is standing text; a pill with `data` is a
  // measurement and reads '—' until the board answers. Both are keyed the same
  // way, and an unsampled label renders as the untoned pill.
  caller_identification: {
    'Status': { 'registration': 'plain', 'reviewing': 'amber', 'verified': 'green' },
  },
  ceo_connector_registry: {
    'badge': { 'read-only registry': 'teal' },
  },
  cold_call_checklist: {
    'badge': { 'today': 'badgePlain' },
  },
  consultation_agenda: {
    'badge': { 'screen-share': 'badgePlain' },
  },
  crm_job_analytics: {
    'Signal': { 'duration watch': 'amber', 'stable': 'green', 'strong': 'green' },
  },
  daily_reports: {
    'badge': { 'today draft': 'badgeAmber' },
  },
  decay_tracker: {
    'badge': { 'CEO threshold: day 15 or 3 missed pulses': 'badgeAmber' },
    'Escalation': { 'CEO escalated': 'red', 'normal': 'green', 'watch': 'amber' },
  },
  // ── QUESTION TAGS · the slot is `tag`, and its TEXT is declared ───────────
  // The first pill whose text does not arrive from the board. `q`, `tests` and
  // `tag` are all transcription, so the tag's WORDS live in surface.ts beside
  // the question; only its TONE is decided here, through the same single entry
  // point every other value uses. One place still says how a value is toned.
  //
  // Six questions, six pills, each sampled exactly once — thin, but every tone
  // is READ off the design rather than derived, so none needed a ruling.
  discovery_questions: {
    'tag': { 'highest value': 'teal', 'baseline': 'teal', 'workflow': 'plain',
             'safety': 'amber', 'commercial': 'plain', 'memory': 'plain' },
  },
  doc_change_detection: {
    'badge': { 'sample data': 'badgeAmber' },
    'Priority': { 'Critical': 'red', 'High': 'amber', 'Low': 'plain', 'Medium': 'plain' },
  },
  // RED ENTERS THE VOCABULARY HERE, AND IT IS SAMPLED. The Fit band carries no
  // red because the design showed no red Fit; the ledger shows `review` in red,
  // so red is taken from the design rather than invented to round out a scale.
  dreamstate_patterns: {
    'badge': { 'read-only': 'badgePlain' },
  },
  emergency_registry: {
    'badge': { 'protected': 'badgeRed' },
  },
  event_ledger: {
    // REVEALED BY ADOPTING THE DESIGN'S TITLE SUFFIX. The design badges "Rexy
    // event ledger — placeholder"; the surface dropped the suffix, so the panel
    // never matched and its badge sat unvouched on the orphan allowlist. Taking
    // the design's own words closed it — a reconciliation that FELL OUT of
    // following the design rather than a fix applied on top of it.
    'badge': { 'source-linked': 'badgeViolet' },
    'state': { 'verified': 'green', 'complete': 'green', 'watch': 'amber', 'review': 'red' },
  },
  gap_folders: {
    'Current status': { 'blocking': 'red', 'draft pending': 'amber', 'owner needed': 'amber' },
  },
  geographic_distribution: {
    'Status': { 'Healthy': 'green' },
  },
  hourly_nodes: {
    'badge': { '24 nodes': 'badgePlain' },
  },
  internet_verification: {
    'Status': { 'high confidence': 'green' },
    'Website?': { 'Yes': 'green' },
  },
  latest_ceo_notes: {
    'badge': { 'source-linked': 'badgeViolet' },
  },
  live_consultation_notes: {
    'State': { 'confirmed': 'green', 'open': 'amber', 'owner action': 'teal' },
  },
  live_transaction_log: {
    'Result': { 'known': 'plain', 'valid': 'green', 'verified': 'green' },
  },
  live_vs_post_call_ownership: {
    'badge': { 'hard boundary': 'badgePlain' },
  },
  maps_discovery: {
    'badge': { 'multi-sector campaign': 'badgePlain' },
  },
  memory_plugin_boundary: {
    'badge': { 'MCR-safe': 'badgeViolet' },
  },
  // ── CARD TAGS · the slot is `cards`, keyed by the tag's text ─────────────
  // The design's record-card carries one amber pill. It goes through the same
  // single entry point as every other value rather than being declared beside
  // the card, so nothing new has to be kept in step.
  p2_finance_inventory: {
    'cards': { 'P2 expansion': 'amber' },
  },
  package_signal: {
    'badge': { 'Intermediate': 'badgeAmber' },
  },
  per_receptionist_cost_watch: {
    'Recommendation': { 'move to Max': 'amber' },
  },
  plugin_build_order: {
    'badge': { 'implementation order': 'badgeAmber' },
  },
  qualified_lists: {
    'Next action': { 'compile Sales handoff': 'green' },
  },
  realtime_number_status: {
    'badge': { '1 degraded': 'badgeAmber' },
  },
  reception_registry: {
    'badge': { 'registry only': 'teal' },
  },
  regional_coverage: {
    'badge': { 'live coverage grid': 'badgePlain' },
  },
  regional_grid: {
    'Coverage': { 'complete': 'green' },
  },
  research_evidence_ledger: {
    'State': { 'fact': 'green', 'hypothesis': 'amber', 'unknown': 'red' },
  },
  sales_call_brief: {
    'badge': { 'v3 ready': 'badgeGreen' },
  },
  sales_connector_registry: {
    'badge': { 'launch map': 'teal' },
  },
  sales_funnel: {
    'badge': { 'live rollup': 'badgeGreen' },
  },
  sales_operating_path: {
    'badge': { 'owner-led sales': 'badgePlain' },
  },
  sector_integration_intelligence: {
    'badge': { 'pitch intelligence': 'badgePlain' },
  },
  sector_matrices: {
    'badge': { '4-sector scope': 'badgeViolet' },
  },
  server_dgx_monitor: {
    'State': { 'healthy': 'green', 'verified': 'green' },
  },
  server_monitoring_system: {
    'badge': { 'selection pending': 'badgeAmber' },
  },
  shared_core: {
    'badge': { 'P1 launch core': 'badgePlain' },
    'Admin': { 'P1 · POST': 'teal' },
    'CEO': { 'P1': 'teal' },
    'Reception': { 'P1 · LIVE': 'teal' },
    'Sales': { 'P1': 'teal', 'P2': 'amber' },
  },
  stripe_payment_state: {
    'State': { 'confirmed': 'green', 'sent': 'amber' },
  },
  transcript_to_admin_queue: {
    'Status': { 'pending': 'amber', 'sent': 'green' },
  },
  // ── THE LIVE MESSAGE CONSOLE · the slot is `state` ───────────────────────
  // Six words, sampled off the design's own message rows.
  //
  // `sent` IS PLAIN HERE. It is AMBER in stripe_payment_state and GREEN in
  // transcript_to_admin_queue — three tones for one word across three panels,
  // and all three are correct in their own. This is the first round's hazard
  // returning, handled by per-panel keying without a decision being needed.
  twilio_live_console: {
    'state': { 'live': 'green', 'complete': 'green', 'delivered': 'green',
               'closed': 'plain', 'sent': 'plain', 'tracked': 'plain' },
  },
  twilio_usage: {
    // The same suffix adoption revealed a SECOND one: the design badges "Twilio
    // usage API — placeholder" and the surface declared no badge at all.
    'badge': { 'live sample': 'badgePlain' },
    'Health': { 'cost watch': 'amber', 'healthy': 'green' },
  },
  unified_number_dashboard: {
    'badge': { 'live sample': 'badgePlain' },
    'Status': { 'Degraded': 'amber', 'Healthy': 'green' },
  },
  website_opportunity: {
    'badge': { 'secondary opportunity': 'badgePlain' },
    'Route': { 'Website Domain review': 'teal' },
    'Website state': { 'no verified site': 'amber', 'website present': 'green' },
  },
  sales_diagnosis_banner: {
    'banner': { 'research complete': 'green', 'sources': 'plain', 'assumptions to confirm': 'amber' },
  },
  sales_pitch_banner: {
    'banner': { 'script': 'green', 'slide deck': 'green', 'owner approval': 'amber' },
  },
  reception_plugin_banner: {
    'banner': { 'live-call path': 'green', 'plugins': 'teal' },
  },
  admin_agent_banner: {
    'banner': { 'paired': 'green', '{tenant}-A': 'teal' },
  },
  admin_plugins_banner: {
    'banner': { 'post-call': 'teal', 'plugins': 'green' },
  },
  ceo_plugin_banner: {
    'banner': { 'services healthy': 'green', 'read-only': 'teal', 'watches': 'amber' },
  },
  sales_calls_banner: {
    'banner': { 'calls today': 'green', 'owner queues': 'plain', 'objection gaps': 'amber' },
  },
  sales_pipeline_banner: {
    'banner': { 'connected': 'green' },
  },
  sales_comms_banner: {
    'banner': { 'mail healthy': 'green', 'calendar healthy': 'green' },
  },
  sales_pulse_banner: {
    'banner': { 'market pulse': 'green', 'next': 'plain', 'CEO escalation': 'amber' },
  },
  sales_plugin_banner: {
    'banner': { 'core services healthy': 'green', 'optional': 'teal' },
  },
  reception_booking_banner: {
    'banner': { 'calendar live': 'green', 'payments live': 'green' },
  },
  reception_transcripts_banner: {
    'banner': { 'calls today': 'green', 'full transcript view': 'teal' },
  },
  workbook_banner: {
    'banner': { 'sales_intelligence_master.xlsx': 'green' },
  },
  workbook_shell: {
    'CEO state': { 'escalated': 'red' },
    'F · Crowding': { 'rising': 'amber', 'very low': 'green' },
  },
  xero_customer_view: {
    'Status': { 'paid': 'green' },
  },
};

/**
 * AN OWN-PROPERTY LOOKUP, AND EVERY LOOKUP IN THIS FILE GOES THROUGH IT.
 *
 * A bare `LOOKUP[boardValue]` reads the PROTOTYPE CHAIN: a board sending
 * "toString" or "constructor" gets back a FUNCTION, which is truthy, and the
 * caller then treats it as a Visual. Measured, not theorised —
 * `{State:{inferred:'amber'}}.State['toString']` is a function.
 *
 * Found by sweeping the fault class after the same hole turned up in the KPI
 * direction guard (owner ruling 2026-08-25: "that is a fault class, not a
 * one-off — sweep for it"). This is the SHIPPED path for every table cell,
 * ledger state, question tag and panel badge, so the hole was open in all four.
 */
// EXPORTED, NOT COPIED. panels.tsx reads the producer's row-level action flag
// off a board-supplied row and needs exactly this guard; a fourth hand-rolled
// copy beside the three already in that file would be the fault this sweep closed.
// (`laneFilter.ts` deliberately imports nothing at all so it can be driven under
// bare node, and carries its own guard for that reason — the same trade
// callTarget.ts already makes.)
export function own<T>(o: Record<string, T> | undefined, k: string): T | undefined {
  return o && Object.prototype.hasOwnProperty.call(o, k) ? o[k] : undefined;
}

/** The tone for a state value, or null for "render it plain, exactly as today".
 *  `slot` is the named place the value arrived in — a column label for a table
 *  cell, `state` for a ledger row. */
export function statePill(panelKey: string, slot: string, raw: unknown): Visual | null {
  const text = typeof raw === 'string' ? raw : String(raw ?? '');
  return own(own(own(STATE_PILLS, panelKey), slot), text) ?? null;
}


// ─────────────────────────────────────────────────────────────────────────────
// MEASUREMENT COLUMNS — five shapes a word map cannot carry, all OWNER-RULED.
//
// These columns hold a MEASUREMENT, not a state word: "72%", "20 / 20",
// "decay day 15", "1 degraded", "0 → 1 / low". Encoding those literals as
// vocabulary would only ever match the design's own sample and never a live
// value. Each needs a rule, and the design gives one sample apiece — not enough
// to derive a band from. So the rules below are the OWNER'S (2026-08-20), not
// mine: two come from the handover's own doctrine, three are Fit's shape.
//
//   fit         ≥8.5 green · 8.0–8.5 bare · <8.0 amber   (the four design samples)
//   percent     ≥70 green · <70 amber                     (THE SAMPLE GOVERNS: the
//               design pills 72% GREEN, and it is the only Coverage sample there is.
//               An earlier ≥85/70–85-bare band was doctrine-derived and rendered that
//               very sample differently; owner ruling 2026-08-20, the design wins.)
//   ratio       complete green, below amber              (the design's 20 / 20 green)
//   decayDay    0–5 bare · 10 amber · 15 red             (DOCTRINE: the handover's
//               schedule is Day 0 → 2 → 5 → 10 → 15, CEO escalation at 15)
//   badCount    0 bare · 1+ amber · NO RED               (a count of a bad thing; the
//               design shows no red here, so none is invented)
//   crowding    "rising" is amber outright. Otherwise the DESTINATION — the number
//               the trajectory ends on — bands it: ≤2 green, 3–4 teal, ≥5 amber.
//
//               WHY THE DESTINATION AND NOT THE DIRECTION, which is worth stating
//               because the owner's words were "the trajectory carries tone when it
//               rises": A LITERAL "RISES -> TEAL" CONTRADICTS THE DESIGN'S OWN
//               SAMPLES. "0 → 1 / low" rises too, and is GREEN; "3 → 4 / low" rises
//               by the same delta of one, and is TEAL. Direction cannot separate
//               them — the destination can, and does, for all five samples:
//                   0 → 1 / low      dest 1   green
//                   1 / very low     dest 1   green
//                   2 / low          dest 2   green
//                   3 → 4 / low      dest 4   TEAL
//                   4 → 6 / rising   word     amber
//               So this is the rule that reproduces every sample, including the four
//               the owner said already matched. FLAGGED: if "rises -> teal" was meant
//               literally, "0 → 1 / low" would have to go teal too and the design says
//               green. One line to change either way.
//
// EVERY PARSER IS STRICT AND DEGRADES TO null. A cell that does not match its
// column's shape renders as its own text, unstyled — the same rule the state
// vocabulary follows. A band may tone a cell; it may never swallow one.
// ─────────────────────────────────────────────────────────────────────────────

export type MeasureKind = 'fit' | 'percent' | 'ratio' | 'decayDay' | 'badCount' | 'crowding';

/** Keyed PANEL -> SLOT, on the SAME axis as STATE_PILLS, so there is ONE place
 *  that says how a value is toned and nothing to keep in step beside it. Every
 *  slot named below is a table column label; the band parsers are numeric and no
 *  non-table slot carries a measurement today. */
export const MEASURE_SLOTS: Record<string, Record<string, MeasureKind>> = {
  qualification_board:     { 'Fit': 'fit' },
  regional_grid:           { 'Coverage': 'percent' },
  active_campaigns:        { 'Sales-ready': 'ratio' },
  active_prospect_work:    { 'State': 'decayDay' },
  geographic_distribution: { 'Status': 'badCount' },
  workbook_shell:          { 'C · Current crowding': 'crowding' },
};

function bandFit(text: string): Visual | null {
  const n = parseFloat(text);
  if (!isFinite(n)) return null;
  for (const b of FIT_BANDS) if (n >= b.min) return b.tone;
  return null;
}

function bandMeasure(kind: MeasureKind, text: string): Visual | null {
  switch (kind) {
    case 'fit': return bandFit(text);
    case 'percent': {
      const m = /^(\d+(?:\.\d+)?)\s*%$/.exec(text);
      if (!m) return null;
      return parseFloat(m[1]) >= 70 ? 'green' : 'amber';
    }
    case 'ratio': {
      // COMPLETE is the whole claim: done === total. A ratio with a zero total
      // is not "complete", it is unanswerable, and returns null.
      const m = /^(\d+)\s*\/\s*(\d+)$/.exec(text);
      if (!m) return null;
      const done = parseInt(m[1], 10), total = parseInt(m[2], 10);
      if (total <= 0) return null;
      return done >= total ? 'green' : 'amber';
    }
    case 'decayDay': {
      // The doctrine's schedule, not a curve fitted to one sample. Day 15 is the
      // CEO escalation and is the only red in it.
      const m = /decay day\s+(\d+)/i.exec(text);
      if (!m) return null;
      const d = parseInt(m[1], 10);
      return d >= 15 ? 'red' : d >= 10 ? 'amber' : null;
    }
    case 'badCount': {
      const m = /^(\d+)\s+\S/.exec(text);
      if (!m) return null;
      return parseInt(m[1], 10) > 0 ? 'amber' : null;
    }
    case 'crowding': {
      // The trailing segment after the last "/" is the ASSESSMENT word; everything
      // before it is the trajectory, and its LAST number is where it ends up.
      const parts = text.split('/');
      const word = (parts.pop() || '').trim().toLowerCase();
      if (word === 'rising') return 'amber';
      if (word !== 'low' && word !== 'very low') return null;
      const nums = (parts.join('/').match(/\d+/g) || []);
      if (!nums.length) return null;
      const dest = parseInt(nums[nums.length - 1], 10);
      return dest <= 2 ? 'green' : dest <= 4 ? 'teal' : 'amber';
    }
    default: return null;
  }
}

/**
 * THE ONE ENTRY POINT the reader uses for every value, on every surface — table,
 * matrix, workbook sheet and ledger row alike.
 *
 * Order matters and is deliberate: the sampled VOCABULARY is consulted first
 * because it is an exact match on a word the design actually showed, and a
 * measurement parser is a pattern that could otherwise claim a value it should
 * not. A slot may legitimately carry both — `active_prospect_work.State` holds
 * "cold call ready" AND "decay day 15" — and each is answered by the mechanism
 * that knows it.
 *
 * Returns null for anything neither knows, and the value renders its own text.
 */
export function cellTone(panelKey: string, slot: string, raw: unknown): Visual | null {
  const word = statePill(panelKey, slot, raw);
  if (word) return word;
  const kind = own(own(MEASURE_SLOTS, panelKey), slot);
  if (!kind) return null;
  return bandMeasure(kind, typeof raw === 'string' ? raw : String(raw ?? ''));
}
